import { createContext, useContext, useState, useEffect } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { lineKey, priceWithOptions } from "../lib/options";
import { trackAddToCart } from "../lib/analytics";

const CartContext = createContext(null);
const PREFIX_BIZ = { hw_: "biz_halwai", bk_: "biz_bakery", exg: "biz_bodega" };
const bizOf = (i) => PREFIX_BIZ[(i.product_id || i.id || "").slice(0, 3)] || i.business_id;

const load = (key, def) => {
  try { return JSON.parse(localStorage.getItem(key)) ?? def; } catch { return def; }
};

const migrate = (items) => (Array.isArray(items) ? items : []).map((i) =>
  i.line_id ? i : { ...i, line_id: lineKey(i.product_id, i.options || {}, i.note || ""), options: i.options || {}, note: i.note || "" });

export function CartProvider({ children }) {
  const [foodCart, setFoodCart] = useState(() => migrate(load("urban_food_cart", [])));
  const [jewelryCart, setJewelryCart] = useState(() => migrate(load("urban_jewelry_cart", [])));
  const [channel, setChannel] = useState(() => localStorage.getItem("urban_channel") || "pickup");
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [tableNumber, setTableNumber] = useState(() => localStorage.getItem("urban_table") || "");
  const [coupon, setCoupon] = useState(() => localStorage.getItem("urban_coupon") || "");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [comingSoon, setComingSoon] = useState({});
  const [businesses, setBusinesses] = useState([]);
  const [soldOut, setSoldOut] = useState({});

  useEffect(() => {
    api.get("/businesses").then(({ data }) => {
      setBusinesses(data);
      setComingSoon(Object.fromEntries(data.filter((b) => b.ordering === "coming_soon").map((b) => [b.id, b.name])));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/delivery/availability").then(({ data }) => setDeliveryInfo(data)).catch(() => setDeliveryInfo({ available: false }));
  }, []);

  useEffect(() => {
    if (deliveryInfo && !deliveryInfo.available && channel === "delivery") setChannel("pickup");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryInfo]);

  useEffect(() => {
    const ids = Object.keys(comingSoon);
    if (!ids.length) return;
    const gone = foodCart.filter((i) => comingSoon[bizOf(i)]);
    if (!gone.length) return;
    setFoodCart((prev) => prev.filter((i) => !comingSoon[bizOf(i)]));
    const names = [...new Set(gone.map((i) => comingSoon[bizOf(i)]))].join(" and ");
    toast.info(`${names} ${gone.length === 1 ? "item" : "items"} removed from your cart`, { description: `${names} is coming soon — not orderable yet.` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comingSoon, foodCart.length]);

  useEffect(() => { localStorage.setItem("urban_food_cart", JSON.stringify(foodCart)); }, [foodCart]);
  useEffect(() => { localStorage.setItem("urban_jewelry_cart", JSON.stringify(jewelryCart)); }, [jewelryCart]);
  useEffect(() => { localStorage.setItem("urban_channel", channel); }, [channel]);
  useEffect(() => { localStorage.setItem("urban_table", tableNumber); }, [tableNumber]);
  useEffect(() => { localStorage.setItem("urban_coupon", coupon); }, [coupon]);

  const addItem = (setter) => (product, qty = 1, extra = {}) => {
    const soonName = comingSoon[bizOf(product)];
    if (soonName) { toast.error(`${soonName} is coming soon — ${product.name} can't be ordered yet`); return false; }
    if (product.in_stock === false) { toast.error(`${product.name} is sold out today`); return false; }
    const options = extra.options || {};
    const note = (extra.note || "").trim();
    const allergy = (extra.allergy || "").trim();
    const id = lineKey(product.id, options, note + (allergy ? `|allergy:${allergy}` : ""));
    setter((prev) => {
      const found = prev.find((i) => i.line_id === id);
      if (found) {
        return prev.map((i) => (i.line_id === id ? { ...i, quantity: i.quantity + qty } : i));
      }
      return [...prev, {
        line_id: id, product_id: product.id, name: product.name, image: product.image, bogo: !!product.bogo,
        price: priceWithOptions(product.display_price, options),
        regular_price: priceWithOptions(product.regular_price, options),
        on_sale: product.on_sale, business_id: product.business_id,
        business_name: product.business_name || "", quantity: qty,
        options, note, allergy,
      }];
    });
    return true;
  };

  // Server guard at add-to-cart: the kitchen's live answer wins over whatever the page loaded with.
  const addFood = async (product, qty = 1, extra = {}) => {
    try {
      const { data } = await api.post("/cart/validate", { items: [{ product_id: product.id, quantity: qty }], channel });
      const bad = data.items.find((i) => !i.ok);
      if (bad) { toast.error(bad.message); setSoldOut((prev) => ({ ...prev, [product.id]: bad.message })); return false; }
    } catch { /* network hiccup: fall through to the local check, the checkout re-validates */ }
    const ok = addItem(setFoodCart)(product, qty, extra);
    if (ok) trackAddToCart(product, qty, priceWithOptions(product.display_price, extra.options || {}));
    return ok;
  };

  // Re-check every line in the cart against the kitchen; returns {product_id: message} for lines that must be removed.
  const validateCart = async () => {
    if (!foodCart.length) { setSoldOut({}); return {}; }
    const { data } = await api.post("/cart/validate", { items: foodCart.map((i) => ({ product_id: i.product_id, quantity: i.quantity })), channel });
    const bad = Object.fromEntries(data.items.filter((i) => !i.ok).map((i) => [i.product_id, i.message]));
    setSoldOut(bad);
    return bad;
  };

  const updateQty = (setter) => (key, qty) => {
    const match = (i) => (key.includes("|") ? i.line_id === key : i.product_id === key);
    setter((prev) => qty <= 0
      ? prev.filter((i) => !match(i))
      : prev.map((i) => (match(i) ? { ...i, quantity: qty } : i)));
  };

  const replaceFoodCart = (entries) => {
    setFoodCart(entries.map(({ product, qty }) => ({
      line_id: lineKey(product.id, {}, ""),
      product_id: product.id, name: product.name, image: product.image,
      price: product.display_price, regular_price: product.regular_price,
      on_sale: product.on_sale, business_id: product.business_id,
      business_name: product.business_name || "", quantity: qty, options: {}, note: "",
    })));
    setCoupon("");
  };

  const value = {
    foodCart, jewelryCart, channel, setChannel, deliveryInfo, tableNumber, setTableNumber,
    coupon, setCoupon,
    drawerOpen, setDrawerOpen,
    addFood, addJewelry: addItem(setJewelryCart),
    soldOut, validateCart,
    updateFoodQty: updateQty(setFoodCart), updateJewelryQty: updateQty(setJewelryCart),
    replaceFoodCart,
    comingSoon,
    businesses,
    publicBrands: businesses.filter((b) => b.type === "food" && b.is_public),
    clearFood: () => { setFoodCart([]); setCoupon(""); }, clearJewelry: () => setJewelryCart([]),
    foodCount: foodCart.reduce((s, i) => s + i.quantity, 0),
    jewelryCount: jewelryCart.reduce((s, i) => s + i.quantity, 0),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
